import Typography from '@mui/material/Typography';
import ReactMarkdown from 'react-markdown';
import CardMedia from '@mui/material/CardMedia';
import Login from '@ashn/identity-react/Login';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import MuiLink from '@mui/material/Link';
import ASHlinkLandingBackground from '../../assets/ASHLink_BG.webp';
import HitrustLogo from '../../assets/hitrust_logo.webp';
import useIsMobile from '../../hooks/useIsMobile';
import useEnvironment from '../../hooks/useEnvironment';
import { getClearCookies, getIsAuthenticated } from '../../data/apiCalls';

// hidden for launch
// const errorAlertContent = `This username / password combination is not valid. If your ASHLink account was created before 03/10/2024 you must activate your account.`;

const LandingPage = () => {
	const isMobile = useIsMobile();
	const env = useEnvironment();

	let redirectUri = `https://${env}.ashlink.com/auth/callback`;
	if (env === 'prod') {
		redirectUri = 'https://www.ashlink.com/auth/callback';
	}

	useQuery(['IsAuthenticated'], () => getIsAuthenticated(), {
		onSuccess: async response => {
			if (response?.status === 200) {
				window.open('/ASH/default.aspx', '_self', 'noreferrer');
			} else {
				await getClearCookies();
			}
		},
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		retry: false,
	});

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					flexDirection: 'row',
					mx: 3,
				}}
			>
				{!isMobile && (
					<Box
						data-testid='page-banner'
						elevation={0}
						sx={{
							display: 'flex',
							alignItems: 'center',
							backgroundImage: `url(${ASHlinkLandingBackground})`,
							backgroundSize: 'cover',
							color: 'white',
							width: '100%',
							mx: 0,
						}}
					>
						<Stack sx={{ p: 10 }}>
							<Typography component='h2' variant='h3'>
								SAVE TIME - GO ONLINE
							</Typography>
							<Typography component='h3' variant='body1'>
								The ASHLink&reg; website allows you to
								streamline office procedures and conduct
								business electronically.
							</Typography>
							<MuiLink
								target='_blank'
								href='https://hitrustalliance.net/'
								rel='noreferrer'
							>
								<CardMedia
									component='img'
									image={HitrustLogo}
									title='Hitrust logo'
									sx={{
										height: { xs: 20, md: 50 },
										width: { xs: 20, md: 50 },
										marginTop: '20px',
									}}
								/>
							</MuiLink>
						</Stack>
					</Box>
				)}
				<Box
					sx={{
						width: 528,
						justifySelf: isMobile ? 'center' : 'end',
					}}
					elevation={0}
				>
					<Stack sx={{ pl: 4 }} gap={4}>
						{/* hidden for launch */}
						{/* <Alert severity='error' sx={{ width: '100%' }}>
						<Typography>{errorAlertContent}</Typography>
					</Alert> */}
						<Box sx={{ mt: isMobile ? 2 : 0 }}>
							<Login
								apiEnvironment={env}
								redirectUri={redirectUri}
								clientId='ashlink.code'
								customerServicePhoneNumber='888-511-2743'
								customGenericErrorMessage="We're sorry, we are unable to confirm your eligibility. If you are a clinical sub-account user, the primary account must register before you can create your account. Please contact the primary account holder. If you feel this is incorrect, please contact us.<br/>For clinical Users:  844-645-2746<br/>For Fitness Users: 877-329-2746, option 4"
								includeOfflineAccessScope={true}
								overrideMainTitleTo='Log in or Create Account'
								loginHostingPath='/identity'
								disableWrapperStyle
							/>
						</Box>
						{!isMobile && (
							<>
								<Divider sx={{ width: '100%', mb: 3, mt: 3 }} />
								<Box>
									<Typography
										variant='h5'
										component='h1'
										color='primary'
										sx={{ mb: 2 }}
									>
										Re-Register Your Account
									</Typography>
									<Typography variant='body2'>
										If you have an ASHlink account created
										before{' '}
										<Typography
											component='span'
											variant='body2'
											color='#ef6c00'
											fontWeight='bold'
										>
											03/10/2025
										</Typography>{' '}
										that uses a PIN, you must re-register
										your account and set up multi-factor
										authentication.
									</Typography>
									<Button
										variant='outlined'
										component={RouterLink}
										to='identity/select-role/Existing%20ASHLink%20User'
										sx={{
											mt: 2,
											width: '100%',
										}}
									>
										Re-Register
									</Button>
									<Typography
										variant='subtitle2'
										component='div'
									>
										<ReactMarkdown>
											Need extra help? Check out our
											[Existing ASHLink User Set Up
											Guide](ASHLink_Reregistration_Existing_One_Page.pdf)
											or [Two-Step Secure Sign-In Set Up
											Guide](ASHLink_MFA_Setup_One_Page.pdf)
										</ReactMarkdown>
									</Typography>
								</Box>
							</>
						)}
					</Stack>
				</Box>
			</Box>
		</>
	);
};

export default LandingPage;
